import React, { useState, useEffect, useMemo } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import * as XLSX from "xlsx";

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import AppTooltip from "../../components/AppTooltip";
import HubPageHeader from "../../components/HubPageHeader";
import CustomToggleSwitch from "../../components/CustomToggleSwitch";

import {
  Download,
  Search,
  History as HistoryIcon,
  Forward as ForwardIcon,
  Filter as FilterIcon,
  RotateCcw,
  Star,
  Inbox,
  CheckCircle2,
  Tag as TagIcon,
  MessageSquare,
} from "lucide-react";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import SimpleDatePicker from "../../components/SimpleDatePicker";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useDashboard from "../../hooks/useDashboard";
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import moment from "moment";

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const useTableStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  return {
    tableScroll: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      borderRadius: ds.radius?.md ?? 12,
      ...theme.scrollbarStyles,
    },
    excelTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        borderRight: "none",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
      "& .MuiTableCell-head": {
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        whiteSpace: "nowrap",
      },
      "& .MuiTableCell-head:first-child": {
        borderTopLeftRadius: ds.radius?.md ?? 12,
      },
      "& .MuiTableCell-head:last-child": {
        borderTopRightRadius: ds.radius?.md ?? 12,
      },
      "& tbody tr:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    zebraRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },
  };
});

const KpiPill = ({ icon, label, value, accent, isDark }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: 2.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
        color: "#fff",
        boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const StatusBadge = ({ status, isDark }) => {
  const map = {
    open: { label: "Aberto", color: "#2EA85A" },
    pending: { label: "Pendente", color: "#F7C82C" },
    closed: { label: "Fechado", color: isDark ? "#8696a0" : "#667781" },
  };
  const cfg = map[status] || { label: status || "—", color: isDark ? "#8696a0" : "#667781" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        background: hexToRgba(cfg.color, 0.15),
        color: cfg.color,
      }}
    >
      {cfg.label}
    </Box>
  );
};

const Reports = () => {
  const tableClasses = useTableStyles();
  const history = useHistory();
  const themeV4 = useThemeV4();
  const { getReport } = useDashboard();

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const tokens = useMemo(
    () => ({
      pageBg: isDark ? "#121212" : "#ffffff",
      cardBg: isDark ? "#11141b" : "#ffffff",
      cardBgSoft: isDark ? "#161a22" : "#fafbfd",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      shadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 8px 24px rgba(0,0,0,0.45)"
        : "0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)",
      textPrimary: isDark ? "#e9edef" : "#0f172a",
      textSecondary: isDark ? "#8696a0" : "#64748b",
    }),
    [isDark]
  );

  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  // Mesmo padrão visual do ChatiaPopover (Kanban) usado nos filtros v4
  const filterPaperSx = useMemo(
    () => ({
      borderRadius: "14px",
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: "none",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none",
      mt: 1,
      overflow: "hidden",
      "& .MuiAutocomplete-listbox": {
        padding: "6px",
        "& .MuiAutocomplete-option": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          color: isDark ? "#ffffff" : "#1a1a1a",
          "&:hover, &.Mui-focused, &[data-focus='true']": {
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          },
          "&[aria-selected='true']": {
            background: "rgba(6, 207, 156, 0.10)",
          },
        },
      },
    }),
    [isDark]
  );

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);

  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").toDate());
  const [dateTo, setDateTo] = useState(moment().toDate());
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);

  useEffect(() => {
    if (!searchParam || searchParam.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", { params: { searchParam } });
          setOptions(data.contacts);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const buildParams = (overrides = {}) => ({
    searchParam,
    contactId: selectedContactId,
    whatsappId: JSON.stringify(selectedWhatsapp),
    users: JSON.stringify(userIds),
    queueIds: JSON.stringify(queueIds),
    status: JSON.stringify(selectedStatus),
    dateFrom: moment(dateFrom).format("YYYY-MM-DD"),
    dateTo: moment(dateTo).format("YYYY-MM-DD"),
    onlyRated: onlyRated ? "true" : "false",
    page: 1,
    pageSize,
    ...overrides,
  });

  const exportarGridParaExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport(buildParams({ pageSize: 9999999 }));
      const ticketsData = data.tickets.map((ticket) => {
        const createdAt = new Date(ticket.createdAt);
        const closedAt = new Date(ticket.closedAt);
        return {
          id: ticket.id,
          [i18n.t("reports.excel.connection")]: ticket.whatsappName,
          [i18n.t("reports.excel.contact")]: ticket.contactName,
          [i18n.t("reports.excel.user")]: ticket.userName,
          [i18n.t("reports.excel.queue")]: ticket.queueName,
          [i18n.t("reports.excel.status")]: ticket.status,
          [i18n.t("reports.excel.lastMessage")]: ticket.lastMessage,
          [i18n.t("reports.excel.dateOpen")]: createdAt.toLocaleDateString(),
          [i18n.t("reports.excel.timeOpen")]: createdAt.toLocaleTimeString(),
          [i18n.t("reports.excel.dateClose")]: ticket.closedAt === null ? "" : closedAt.toLocaleDateString(),
          [i18n.t("reports.excel.timeClose")]: ticket.closedAt === null ? "" : closedAt.toLocaleTimeString(),
          [i18n.t("reports.excel.supportTime")]: ticket.supportTime,
          [i18n.t("reports.excel.nps")]: ticket.NPS,
        };
      });
      const ws = XLSX.utils.json_to_sheet(ticketsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, i18n.t("reports.excel.sheetName"));
      XLSX.writeFile(wb, i18n.t("reports.excel.fileName"));
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (page) => {
    setLoading(true);
    try {
      const data = await getReport(buildParams({ page }));
      setTotalTickets(data.totalTickets.total);
      setTickets(data.tickets);
      setPageNumber(page);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchParam("");
    setSelectedContactId(null);
    setSelectedWhatsapp([]);
    setSelectedStatus([]);
    setUserIds([]);
    setQueueIds([]);
    setDateFrom(moment("1", "D").toDate());
    setDateTo(moment().toDate());
    setOnlyRated(false);
  };

  const stats = useMemo(() => {
    const open = tickets.filter((t) => t.status === "open").length;
    const closed = tickets.filter((t) => t.status === "closed").length;
    const rated = tickets.filter((t) => t.NPS != null && t.NPS !== "").length;
    return { open, closed, rated, total: totalTickets };
  }, [tickets, totalTickets]);

  const periodLabel = `${moment(dateFrom).format("DD/MM/YYYY")} — ${moment(dateTo).format("DD/MM/YYYY")}`;

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <Box
        sx={{
          background: tokens.pageBg,
          height: "100%",
          width: "100%",
          paddingTop: "12px",
          paddingBottom: "12px",
          paddingLeft: "12px",
          paddingRight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            paddingRight: "12px",
            ...(themeV4.scrollbarStyles || {}),
          }}
        >
          {openTicketMessageDialog && (
            <ShowTicketLogModal
              isOpen={openTicketMessageDialog}
              handleClose={() => setOpenTicketMessageDialog(false)}
              ticketId={ticketOpen.id}
            />
          )}

          <HubPageHeader
            icon={<HistoryIcon size={20} strokeWidth={1.8} />}
            title={tLang("reports.title", "Relatórios")}
            subtitle={`${tLang("reports.subtitle", "Histórico de atendimentos com filtros e exportação")} · ${periodLabel}`}
            action={
              <AppTooltip title={tLang("reports.buttons.export", "Exportar")}>
                <IconButton
                  onClick={exportarGridParaExcel}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                    color: PRIMARY_CONTRAST,
                    borderRadius: "12px",
                    boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                    transition: "all .2s ease",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                      transform: "translateY(-1px)",
                      boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                    },
                  }}
                >
                  <Download size={18} />
                </IconButton>
              </AppTooltip>
            }
          />

          {/* KPIs */}
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<Inbox size={18} />} label={tLang("reports.kpis.total", "Total")} value={stats.total} accent={PRIMARY_MAIN} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<MessageSquare size={18} />} label={tLang("reports.kpis.open", "Em aberto")} value={stats.open} accent="#2EA85A" isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<CheckCircle2 size={18} />} label={tLang("reports.kpis.closed", "Finalizados")} value={stats.closed} accent="#3b82f6" isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KpiPill icon={<Star size={18} />} label={tLang("reports.kpis.rated", "Avaliados")} value={stats.rated} accent="#F7C82C" isDark={isDark} />
            </Grid>
          </Grid>

          {/* FILTROS */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              mb: 2.5,
              borderRadius: 3,
              background: tokens.cardBg,
              border: tokens.border,
              boxShadow: tokens.shadow,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${hexToRgba(PRIMARY_MAIN, 0.7)} 100%)`,
                  color: "#fff",
                  boxShadow: `0 4px 12px ${hexToRgba(PRIMARY_MAIN, 0.3)}`,
                }}
              >
                <FilterIcon size={18} />
              </Avatar>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: tokens.textPrimary }}>
                {tLang("reports.filters", "Filtros")}
              </Typography>
            </Stack>

            <Grid
              container
              spacing={1.5}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "12px !important" },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "12px !important" },
                "& .MuiInputBase-root": { borderRadius: "12px !important" },
              }}
            >
              <Grid item xs={12} md={3}>
                <Autocomplete
                  fullWidth
                  options={options}
                  loading={loading}
                  size="small"
                  clearOnBlur
                  autoHighlight
                  clearOnEscape
                  noOptionsText={
                    !searchParam || searchParam.trim().length < 2
                      ? tLang("reports.contactSearchHint", "Digite ao menos 2 letras")
                      : tLang("reports.table.empty", "Nenhum contato encontrado")
                  }
                  getOptionLabel={(o) => (o.number ? `${o.name} - ${o.number}` : o.name || "")}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  onChange={(e, v) => setSelectedContactId(v ? v.id : null)}
                  componentsProps={{ paper: { sx: filterPaperSx } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tLang("reports.contactSearch", "Buscar contato")}
                      variant="outlined"
                      size="small"
                      onChange={(e) => setSearchParam(e.target.value)}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search size={16} style={{ marginRight: 6, color: tokens.textSecondary }} />,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <WhatsappsFilter onFiltered={(s) => setSelectedWhatsapp(s.map((t) => t.id))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatusFilter onFiltered={(s) => setSelectedStatus(s.map((t) => t.status))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UsersFilter onFiltered={(s) => setUserIds(s.map((t) => t.id))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <QueueSelectCustom selectedQueueIds={queueIds} onChange={setQueueIds} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SimpleDatePicker
                  value={dateFrom}
                  onChange={setDateFrom}
                  label={tLang("reports.form.initialDate", "Data inicial")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SimpleDatePicker
                  value={dateTo}
                  onChange={setDateTo}
                  label={tLang("reports.form.finalDate", "Data final")}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ height: "100%", minHeight: 40, pl: 1 }}
                >
                  <CustomToggleSwitch
                    checked={onlyRated}
                    onChange={() => setOnlyRated(!onlyRated)}
                  />
                  <Typography sx={{ fontSize: 13, color: tokens.textPrimary }}>
                    {tLang("reports.buttons.onlyRated", "Apenas avaliados")}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                onClick={clearFilters}
                variant="text"
                startIcon={<RotateCcw size={16} />}
                sx={{
                  color: tokens.textSecondary,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {tLang("reports.buttons.clear", "Limpar")}
              </Button>
              <Button
                onClick={() => handleFilter(1)}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FilterIcon size={16} />}
                sx={{
                  background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                  color: PRIMARY_CONTRAST,
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                  },
                }}
              >
                {tLang("reports.buttons.filter", "Filtrar")}
              </Button>
            </Stack>
          </Box>

          {/* TABELA */}
          <Box
            sx={{
              borderRadius: 3,
              background: tokens.cardBg,
              border: tokens.border,
              boxShadow: tokens.shadow,
              overflow: "hidden",
              mb: 2.5,
            }}
          >
            <TableContainer className={tableClasses.tableScroll}>
              <Table className={tableClasses.excelTable} size="small" id="grid-attendants">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">{tLang("reports.table.id", "ID")}</TableCell>
                    <TableCell>{tLang("reports.table.whatsapp", "Conexão")}</TableCell>
                    <TableCell>{tLang("reports.table.contact", "Contato")}</TableCell>
                    <TableCell>{tLang("reports.table.user", "Atendente")}</TableCell>
                    <TableCell>{tLang("reports.table.queue", "Fila")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.status", "Status")}</TableCell>
                    <TableCell>{tLang("reports.table.lastMessage", "Última mensagem")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.dateOpen", "Aberto em")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.dateClose", "Fechado em")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.supportTime", "Tempo")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.NPS", "NPS")}</TableCell>
                    <TableCell align="center">{tLang("reports.table.actions", "Ações")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRowSkeleton avatar columns={11} />
                  ) : tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: "center", py: 6, color: tokens.textSecondary, fontStyle: "italic" }}>
                        {tLang("reports.table.empty", "Nenhum atendimento encontrado.")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((ticket) => (
                      <TableRow key={ticket.id} className={tableClasses.zebraRow}>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{ticket.id}</TableCell>
                        <TableCell>{ticket?.whatsappName}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{ticket?.contactName}</TableCell>
                        <TableCell>{ticket?.userName}</TableCell>
                        <TableCell>
                          {ticket?.queueName && (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 12 }}>
                              <TagIcon size={12} style={{ opacity: 0.6 }} />
                              {ticket.queueName}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center"><StatusBadge status={ticket?.status} isDark={isDark} /></TableCell>
                        <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: tokens.textSecondary }}>
                          {ticket?.lastMessage}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 12, color: tokens.textSecondary }}>{ticket?.createdAt && moment(ticket.createdAt).format("DD/MM/YY HH:mm")}</TableCell>
                        <TableCell align="center" sx={{ fontSize: 12, color: tokens.textSecondary }}>{ticket?.closedAt && moment(ticket.closedAt).format("DD/MM/YY HH:mm")}</TableCell>
                        <TableCell align="center">{ticket?.supportTime}</TableCell>
                        <TableCell align="center">
                          {ticket?.NPS != null && ticket?.NPS !== "" ? (
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "#F7C82C", fontWeight: 700 }}>
                              <Star size={12} /> {ticket.NPS}
                            </Box>
                          ) : "—"}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <AppTooltip titleKey="reports.tooltips.ticketLogs">
                              <IconButton
                                size="small"
                                onClick={() => { setOpenTicketMessageDialog(true); setTicketOpen(ticket); }}
                                sx={{ color: "#3b82f6", borderRadius: "10px", "&:hover": { background: hexToRgba("#3b82f6", 0.1) } }}
                              >
                                <HistoryIcon size={16} />
                              </IconButton>
                            </AppTooltip>
                            <AppTooltip titleKey="reports.tooltips.accessTicket">
                              <IconButton
                                size="small"
                                onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                                sx={{ color: "#2EA85A", borderRadius: "10px", "&:hover": { background: hexToRgba("#2EA85A", 0.1) } }}
                              >
                                <ForwardIcon size={16} />
                              </IconButton>
                            </AppTooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* PAGINAÇÃO */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mt: 1 }}
          >
            <Pagination
              count={Math.max(1, Math.ceil(totalTickets / pageSize))}
              page={pageNumber}
              onChange={(e, value) => handleFilter(value)}
              shape="rounded"
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "10px",
                  fontWeight: 600,
                },
              }}
            />
            <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
              <InputLabel>{i18n.t("tickets.search.ticketsPerPage")}</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                label={i18n.t("tickets.search.ticketsPerPage")}
                sx={{ borderRadius: "12px" }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Box>
    </ThemeProviderV5>
  );
};

export default Reports;
